import type { ListBuilderConfiguration } from '../types';
import { allLicensesList } from './license/lists/all-licenses';

/**
 * Client Licenses (embedded on the client detail page) reuses the compliance
 * "All Licenses" column set so the two surfaces stay visually consistent. The
 * `licenseClient` filter is forced server-side by the controller, so there are
 * no workflow tabs and the redundant Client column is dropped.
 */

const filteredColumns = (allLicensesList.columns ?? []).filter(
	(column) => column.id !== 'license-client',
);

export const CLIENT_LICENSES_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			...allLicensesList,
			id: 'default-client-licenses',
			name: 'Client Licenses',
			sortOrder: 0,
			// The compliance "All Licenses" tab applies a Sales-Rep gate via
			// filterRules. The client tab is already gated by clients:licenses:read,
			// so we don't re-apply that gate here.
			filterRules: undefined,
			columns: filteredColumns.map((column) => {
				if (column.id !== 'license-actions') {
					return column;
				}

				// Swap action permissions to the clients:licenses:* scope and drop the
				// reviewMatch action (compliance-only workflow).
				return {
					...column,
					fields: column.fields.map((field) => {
						if (field.id !== 'actions-dropdown') {
							return field;
						}

						return {
							...field,
							dropdownActions: (field.dropdownActions ?? [])
								.filter((action) => action.id !== 'review-match-action')
								.map((action) => {
									if (action.id === 'edit-action') {
										return {
											...action,
											requiredPermission: 'clients:licenses:update',
										};
									}

									if (action.id === 'delete-action') {
										return {
											...action,
											requiredPermission: 'clients:licenses:delete',
										};
									}

									return action;
								}),
						};
					}),
				};
			}),
			actions: [],
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [10, 25, 50, 100],
	},
	showResetButton: true,
	filters: {
		activeFilters: [
			'licenseKeywords',
			'licenseStatus',
			'licenseType',
			'licenseState',
			'licenseExpirationDateRange',
			'licenseHasDisciplinary',
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/licenses/:id',
			objectLabel: 'License',
			objectTable: 'client_license',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'licenseNumber', fieldLabel: 'License #' },
				{ fieldName: 'type', fieldLabel: 'Type' },
				{ fieldName: 'status', fieldLabel: 'Status' },
				{ fieldName: 'expirationDate', fieldLabel: 'Expiration Date' },
				{ fieldName: 'issueDate', fieldLabel: 'Issue Date' },
				{ fieldName: 'issuingState', fieldLabel: 'Issuing State' },
				{ fieldName: 'issuingAuthority', fieldLabel: 'Issuing Authority' },
				{ fieldName: 'hasDisciplinaryAction', fieldLabel: 'Has Disciplinary' },
			],
		},
	],
};
